import { useEffect, useState } from "react";
import api from "@/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Save, KeyRound, Check, X, Mail, Lock } from "lucide-react";
import { toast } from "sonner";

export default function SettingsPage() {
    const [s, setS] = useState(null);
    const [resendKey, setResendKey] = useState("");
    const [saving, setSaving] = useState(false);

    // password change
    const [pwCurrent, setPwCurrent] = useState("");
    const [pwNew, setPwNew] = useState("");
    const [pwConfirm, setPwConfirm] = useState("");
    const [pwSaving, setPwSaving] = useState(false);

    const load = async () => {
        const { data } = await api.get("/settings");
        setS(data);
    };
    useEffect(() => { load(); }, []);

    if (!s) return <div className="text-slate-400 text-sm">Lädt ...</div>;

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...s };
            if (resendKey) payload.resend_api_key = resendKey;
            await api.put("/settings", payload);
            setResendKey("");
            toast.success("Einstellungen gespeichert");
            load();
        } catch { toast.error("Speichern fehlgeschlagen"); }
        finally { setSaving(false); }
    };

    const changePassword = async () => {
        if (!pwCurrent || !pwNew) { toast.error("Bitte beide Felder ausfüllen"); return; }
        if (pwNew.length < 6) { toast.error("Neues Passwort muss mindestens 6 Zeichen haben"); return; }
        if (pwNew !== pwConfirm) { toast.error("Neue Passwörter stimmen nicht überein"); return; }
        setPwSaving(true);
        try {
            await api.post("/auth/change-password", { current_password: pwCurrent, new_password: pwNew });
            toast.success("Passwort erfolgreich geändert");
            setPwCurrent(""); setPwNew(""); setPwConfirm("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Passwort-Änderung fehlgeschlagen");
        } finally { setPwSaving(false); }
    };

    return (
        <div className="space-y-8 animate-fade-in-up max-w-3xl" data-testid="settings-page">
            <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-[#007bff] font-bold mb-2">Konfiguration</div>
                <h1 className="font-display text-5xl text-[#1a2b48] leading-none">Einstellungen.</h1>
                <p className="text-slate-500 mt-3 max-w-xl">Versand-Konfiguration und Resend-Integration.</p>
            </div>

            <Card className="p-7 bg-white border-slate-200 rounded-xl">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                        <KeyRound className="w-5 h-5 text-[#007bff]" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-[#1a2b48]">Resend API Integration</h3>
                        <p className="text-xs text-slate-500">https://resend.com/api-keys</p>
                    </div>
                    <div className="ml-auto">
                        {s.resend_api_key_set ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-100">
                                <Check className="w-3.5 h-3.5" />Konfiguriert
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-100">
                                <X className="w-3.5 h-3.5" />Noch nicht gesetzt
                            </span>
                        )}
                    </div>
                </div>
                <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Resend API Key</Label>
                    <Input data-testid="resend-key-input" type="password" placeholder="re_..." value={resendKey} onChange={(e) => setResendKey(e.target.value)} className="font-mono" />
                    <p className="text-xs text-slate-500 leading-relaxed pt-1">
                        Hinweis: Im Resend-Dashboard die Domain <strong>366transfer.com</strong> verifizieren, damit Sie von <strong>admin@366transfer.com</strong> versenden dürfen.
                        Webhook-URL für Tracking: <code className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{`${process.env.REACT_APP_BACKEND_URL}/api/webhooks/resend`}</code>
                    </p>
                </div>
            </Card>

            <Card className="p-7 bg-white border-slate-200 rounded-xl">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><Mail className="w-5 h-5 text-[#007bff]" /></div>
                    <h3 className="font-semibold text-[#1a2b48]">Absender-Informationen</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Absender-Name</Label>
                        <Input data-testid="sender-name-input" value={s.sender_name || ""} onChange={(e) => setS({ ...s, sender_name: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Absender-E-Mail</Label>
                        <Input data-testid="sender-email-input" type="email" value={s.sender_email || ""} onChange={(e) => setS({ ...s, sender_email: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Reply-To</Label>
                        <Input value={s.reply_to || ""} onChange={(e) => setS({ ...s, reply_to: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Portal-URL</Label>
                        <Input value={s.portal_url || ""} onChange={(e) => setS({ ...s, portal_url: e.target.value })} />
                    </div>
                </div>
            </Card>

            <div className="flex justify-end">
                <Button onClick={save} disabled={saving} data-testid="settings-save-btn"
                    className="bg-[#1a2b48] hover:bg-[#2a3d60] h-11 px-6 shadow-md shadow-[#1a2b48]/10">
                    <Save className="w-4 h-4 mr-2" />{saving ? "Speichert ..." : "Einstellungen speichern"}
                </Button>
            </div>

            {/* Password change */}
            <Card className="p-7 bg-white border-slate-200 rounded-xl">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center">
                        <Lock className="w-5 h-5 text-rose-600" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-[#1a2b48]">Passwort ändern</h3>
                        <p className="text-xs text-slate-500">Mindestens 6 Zeichen</p>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Aktuelles Passwort</Label>
                        <Input data-testid="current-password-input" type="password" autoComplete="current-password"
                            value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Neues Passwort</Label>
                        <Input data-testid="new-password-input" type="password" autoComplete="new-password"
                            value={pwNew} onChange={(e) => setPwNew(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Bestätigen</Label>
                        <Input data-testid="confirm-password-input" type="password" autoComplete="new-password"
                            value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} />
                    </div>
                </div>
                <div className="flex justify-end mt-6">
                    <Button onClick={changePassword} disabled={pwSaving} data-testid="change-password-btn"
                        className="bg-rose-600 hover:bg-rose-700 h-11 px-6 shadow-md shadow-rose-500/20">
                        <Lock className="w-4 h-4 mr-2" />{pwSaving ? "Ändert ..." : "Passwort ändern"}
                    </Button>
                </div>
            </Card>
        </div>
    );
}
